import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { CategoryService } from '../../../services/category.service';
import { ProductService } from '../../../services/product.service';
import { Category } from '../../../interfaces/category.interface';
import { Product } from '../../../interfaces/product.interface';
import { ToastService } from '../../../services/toast.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-category-management',
  templateUrl: './category-management.component.html',
  styleUrls: ['./category-management.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class CategoryManagementComponent implements OnInit, OnDestroy {
  categories: Category[] = [];
  filteredCategories: Category[] = [];
  products: Product[] = [];
  loading = false;
  error: string | null = null;
  
  // Form properties
  categoryForm!: FormGroup;
  multilingualForm!: FormGroup;
  isEditing = false;
  editingCategoryId: string | null = null;
  
  // Search and filter properties
  searchTerm = '';
  selectedProductId: string | 'all' = 'all';
  selectedStatus: 'all' | 'active' | 'inactive' = 'all';
  
  // Modal properties
  selectedCategory: Category | null = null;
  
  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  
  private destroy$ = new Subject<void>();

  constructor(
    private categoryService: CategoryService,
    private productService: ProductService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
    this.initMultilingualForm();
  }

  private initForm(): void {
    this.categoryForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      price: [0, [Validators.required, Validators.min(0)]],
      originalPrice: [0, [Validators.min(0)]],
      subProducts: this.formBuilder.array([
        this.formBuilder.control('', Validators.required) // First field is mandatory
      ], [Validators.required, this.subProductsArrayValidator.bind(this)]),
      productIds: [[], [Validators.required, this.productArrayValidator.bind(this)]], // Array of product IDs, at least 1 required
      isActive: [true],
      i18n: [null] // Multilingual content
    });
  }

  // Getter for subProducts FormArray
  get subProductsFormArray(): FormArray {
    return this.categoryForm.get('subProducts') as FormArray;
  }

  // Custom validator for subProducts array (at least 1 sub-product required, first one mandatory)
  private subProductsArrayValidator() {
    return (control: any) => {
      const formArray = control as FormArray;
      if (!formArray || formArray.length === 0) {
        return { noSubProducts: true };
      }
      
      // Check if first field has value
      const firstControl = formArray.at(0);
      if (!firstControl || !firstControl.value || firstControl.value.trim() === '') {
        return { firstSubProductRequired: true };
      }
      
      return null;
    };
  }

  // Custom validator for product array (at least 1 product required)
  private productArrayValidator() {
    return (control: any) => {
      const value = control.value;
      
      if (!value || !Array.isArray(value) || value.length === 0) {
        return { noProducts: true };
      }
      
      return null;
    };
  }

  private initMultilingualForm(): void {
    this.multilingualForm = this.formBuilder.group({
      // Hindi fields
      hi_name: [''],
      hi_subProducts: this.formBuilder.array([
        this.formBuilder.control('', Validators.required) // First field is mandatory
      ], [Validators.required, this.subProductsArrayValidator.bind(this)]),
      // Marathi fields
      mr_name: [''],
      mr_subProducts: this.formBuilder.array([
        this.formBuilder.control('', Validators.required) // First field is mandatory
      ], [Validators.required, this.subProductsArrayValidator.bind(this)]),
      // Kannada fields
      kn_name: [''],
      kn_subProducts: this.formBuilder.array([
        this.formBuilder.control('', Validators.required) // First field is mandatory
      ], [Validators.required, this.subProductsArrayValidator.bind(this)])
    });
  }

  // Getter for Hindi subProducts FormArray
  get hiSubProductsFormArray(): FormArray {
    return this.multilingualForm.get('hi_subProducts') as FormArray;
  }

  // Getter for Marathi subProducts FormArray
  get mrSubProductsFormArray(): FormArray {
    return this.multilingualForm.get('mr_subProducts') as FormArray;
  }

  // Getter for Kannada subProducts FormArray
  get knSubProductsFormArray(): FormArray {
    return this.multilingualForm.get('kn_subProducts') as FormArray;
  }

  ngOnInit(): void {
    this.loadCategories();
    this.loadProducts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadCategories(): Promise<void> {
    this.loading = true;
    this.error = null;
    
    try {
      // Always ensure products are loaded first (from coreProducts collection)
      await this.loadProducts();
      console.log('Products loaded for category display:', this.products.length);
      console.log('Product IDs available:', this.products.map(p => ({ id: p.id, name: p.name })));
      
      // Clear cache and force refresh
      this.categoryService.clearCache();
      await this.categoryService.refreshCategories();
      
      this.categoryService.getCategories()
        .pipe(takeUntil(this.destroy$))
        .subscribe(categories => {
          console.log('Categories loaded:', categories.length);
          categories.forEach(cat => {
            console.log(`Category: ${cat.name}`, {
              productIds: cat.productIds,
              productIdsType: typeof cat.productIds,
              isArray: Array.isArray(cat.productIds),
              subProducts: cat.subProducts,
              subProductsType: typeof cat.subProducts,
              subProductsIsArray: Array.isArray(cat.subProducts)
            });
          });
          // Sort by price (low to high), same as product detail
          const sorted = categories.slice().sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
          this.categories = sorted;
          this.filteredCategories = sorted;
          this.loading = false;
          this.cdr.detectChanges();
        });
    } catch (error) {
      this.error = 'Failed to load categories';
      this.loading = false;
      console.error('Error loading categories:', error);
    }
  }

  async loadProducts(): Promise<void> {
    try {
      return new Promise<void>((resolve) => {
        this.productService.getProducts()
          .pipe(takeUntil(this.destroy$))
          .subscribe(products => {
            this.products = products;
            resolve();
          });
      });
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }

  // Search and filter methods
  filterCategories(): void {
    let filtered = this.categories;

    // Search by name or sub-products
    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(category => 
        category.name.toLowerCase().includes(searchLower) ||
        category.subProducts.some((sp: string) => sp.toLowerCase().includes(searchLower))
      );
    }

    // Filter by product
    if (this.selectedProductId !== 'all') {
      filtered = filtered.filter(category => {
        const productIds = category.productIds || ((category as any).productId ? [(category as any).productId] : []);
        return productIds.includes(this.selectedProductId);
      });
    }

    // Filter by status
    if (this.selectedStatus !== 'all') {
      const isActive = this.selectedStatus === 'active';
      filtered = filtered.filter(category => category.isActive === isActive);
    }

    // Keep sort by price (low to high)
    this.filteredCategories = filtered.slice().sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    this.currentPage = 1;
  }

  // CRUD Operations
  openAddModal(content: any): void {
    this.isEditing = false;
    this.editingCategoryId = null;
    this.selectedCategory = null;
    this.resetForm();
    
    const modalRef = this.modalService.open(content, { size: 'lg', scrollable: true });
    
    modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetForm();
    });
  }

  openEditModal(content: any, category: Category): void {
    this.isEditing = true;
    this.editingCategoryId = category.id;
    this.selectedCategory = category;
    this.populateForm(category);
    
    const modalRef = this.modalService.open(content, { size: 'lg', scrollable: true });
    
    modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetForm();
    });
  }

  openDeleteModal(content: any, category: Category): void {
    this.selectedCategory = category;
    this.modalService.open(content, { size: 'sm' });
  }

  async saveCategory(): Promise<void> {
    if (this.categoryForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    try {
      const formValue = this.categoryForm.value;
      
      // Get sub-products from FormArray and filter out empty values
      const subProductsArray = this.subProductsFormArray.value;
      const subProducts = subProductsArray
        .map((sp: string) => sp?.trim())
        .filter((sp: string) => sp && sp.length > 0);
      
      if (subProducts.length === 0) {
        this.toastService.error('Please enter at least one sub-product');
        return;
      }

      // Ensure productIds is always an array
      let productIds: string[] = [];
      if (formValue.productIds) {
        productIds = Array.isArray(formValue.productIds) 
          ? formValue.productIds 
          : (typeof formValue.productIds === 'string' ? [formValue.productIds] : []);
      }
      
      if (productIds.length === 0) {
        this.toastService.error('Please select at least one product');
        return;
      }
      
      const categoryData: Partial<Category> = {
        name: formValue.name,
        price: Number(formValue.price),
        originalPrice: formValue.originalPrice ? Number(formValue.originalPrice) : undefined,
        subProducts: subProducts,
        productIds: productIds,
        isActive: formValue.isActive
      };

      // Only include i18n if it has actual content (not null/undefined/empty)
      if (formValue.i18n && Object.keys(formValue.i18n).length > 0) {
        categoryData.i18n = formValue.i18n;
      }

      let categoryId: string;
      if (this.isEditing && this.editingCategoryId) {
        categoryId = this.editingCategoryId;
        await this.categoryService.updateCategory(this.editingCategoryId, categoryData);
        this.toastService.success('Category updated successfully!');
      } else {
        categoryId = await this.categoryService.createCategory(categoryData as Omit<Category, 'id'>);
        this.toastService.success('Category created successfully!');
      }

      // Sync product.categoryIds so product detail and admin product form stay in sync
      const oldProductIds = this.getCategoryProductIds(this.selectedCategory || {} as Category);
      const newProductIds = productIds;
      for (const prodId of newProductIds) {
        if (!oldProductIds.includes(prodId)) {
          await this.productService.addCategoryToProduct(prodId, categoryId);
        }
      }
      for (const prodId of oldProductIds) {
        if (!newProductIds.includes(prodId)) {
          await this.productService.removeCategoryFromProduct(prodId, categoryId);
        }
      }

      // Close modal and reset form
      this.modalService.dismissAll();
      this.resetForm();
      
      // Force refresh categories list
      await this.loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      this.toastService.error('Failed to save category. Please try again.');
    }
  }

  async deleteCategory(categoryId: string): Promise<void> {
    try {
      await this.categoryService.deleteCategory(categoryId);
      this.toastService.success('Category deleted successfully!');
      this.modalService.dismissAll();
      this.loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      this.toastService.error('Failed to delete category. Please try again.');
    }
  }

  // Form helper methods
  private populateForm(category: Category): void {
    // Handle both productIds array and legacy productId string
    const productIds = category.productIds || ((category as any).productId ? [(category as any).productId] : []);
    
    // Clear existing subProducts FormArray
    while (this.subProductsFormArray.length !== 0) {
      this.subProductsFormArray.removeAt(0);
    }
    
    // Populate subProducts FormArray from category data
    const subProductsArray = Array.isArray(category.subProducts) 
      ? category.subProducts 
      : (category.subProducts ? [category.subProducts] : []);
    
    if (subProductsArray.length === 0) {
      // If no sub-products, add one empty mandatory field
      this.subProductsFormArray.push(this.formBuilder.control('', Validators.required));
    } else {
      // Add each sub-product as a form control
      subProductsArray.forEach((subProduct, index) => {
        const validators = index === 0 ? [Validators.required] : []; // First one is mandatory
        this.subProductsFormArray.push(this.formBuilder.control(subProduct || '', validators));
      });
    }
    
    this.categoryForm.patchValue({
      name: category.name || '',
      price: category.price || 0,
      originalPrice: category.originalPrice || 0,
      productIds: productIds,
      isActive: category.isActive !== undefined ? category.isActive : true,
      i18n: category.i18n || null
    });
  }

  private resetForm(): void {
    // Clear subProducts FormArray
    while (this.subProductsFormArray.length !== 0) {
      this.subProductsFormArray.removeAt(0);
    }
    
    // Add one mandatory field
    this.subProductsFormArray.push(this.formBuilder.control('', Validators.required));
    
    this.categoryForm.reset();
    this.categoryForm.patchValue({
      isActive: true,
      price: 0,
      originalPrice: 0
    });
    this.categoryForm.markAsUntouched();
    this.categoryForm.markAsPristine();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.categoryForm.controls).forEach(key => {
      const control = this.categoryForm.get(key);
      if (control instanceof FormArray) {
        control.controls.forEach(subControl => {
          subControl.markAsTouched();
        });
      } else {
        control?.markAsTouched();
      }
    });
  }

  // Add a new sub-product field
  addSubProduct(): void {
    this.subProductsFormArray.push(this.formBuilder.control('')); // Optional fields don't need validators
    this.cdr.detectChanges();
  }

  // Remove a sub-product field (only if more than 1 exists)
  removeSubProduct(index: number): void {
    if (this.subProductsFormArray.length > 1) {
      this.subProductsFormArray.removeAt(index);
      this.cdr.detectChanges();
    }
  }

  // Check if delete button should be enabled (only if more than 1 sub-product)
  canDeleteSubProduct(): boolean {
    return this.subProductsFormArray.length > 1;
  }

  // Utility methods
  // Get product names for multiple product IDs
  // Matches productIds with coreProducts collection and fetches names
  getProductNames(productIds: string[] | any): string {
    console.log('getProductNames called with:', productIds);
    console.log('Available products:', this.products.length);
    
    // Ensure productIds is always an array
    if (!productIds) {
      console.log('No productIds provided');
      return 'No Products';
    }
    
    // Convert to array if it's not already
    const idsArray = Array.isArray(productIds) 
      ? productIds 
      : (typeof productIds === 'string' ? [productIds] : []);
    
    console.log('Normalized productIds array:', idsArray);
    
    if (idsArray.length === 0) {
      console.log('Empty productIds array');
      return 'No Products';
    }
    
    // If products aren't loaded yet, show IDs temporarily
    if (this.products.length === 0) {
      console.log('Products not loaded yet, showing IDs');
      return idsArray.join(', ');
    }
    
    // Match each productId with products from coreProducts collection
    const productNames = idsArray.map((id: string) => {
      const product = this.products.find(p => p.id === id);
      if (product) {
        console.log(`Found product for ID ${id}: ${product.name}`);
        return product.name;
      } else {
        console.log(`Product not found for ID: ${id}`);
        return `ID: ${id}`;
      }
    });
    
    const result = productNames.join(', ');
    console.log('Final product names result:', result);
    return result;
  }

  // Helper method to get productIds from category (handles both productIds array and legacy productId)
  getCategoryProductIds(category: Category): string[] {
    // Handle productIds array
    if (category.productIds !== undefined && category.productIds !== null) {
      return Array.isArray(category.productIds) ? category.productIds : [];
    }
    
    // Handle legacy productId (single string)
    const legacyProductId = (category as any).productId;
    if (legacyProductId) {
      return typeof legacyProductId === 'string' ? [legacyProductId] : [];
    }
    
    return [];
  }

  // Get product name for a single product ID
  getProductName(productId: string): string {
    if (!productId) {
      return 'Unknown Product';
    }
    
    // If products aren't loaded yet, show ID temporarily
    if (this.products.length === 0) {
      return `ID: ${productId}`;
    }
    
    const product = this.products.find(p => p.id === productId);
    if (product) {
      return product.name;
    } else {
      return `ID: ${productId}`;
    }
  }

  // Check if product is selected (handles form value as array or object from Firebase)
  isProductSelected(productId: string): boolean {
    return this.getProductIdsFormValue().includes(productId);
  }

  // Get productIds from form as normalized array (handles object from Firebase)
  private getProductIdsFormValue(): string[] {
    const raw = this.categoryForm.get('productIds')?.value;
    if (!raw) return [];
    return Array.isArray(raw)
      ? raw
      : (typeof raw === 'object' && raw !== null
          ? (Object.values(raw).filter((id): id is string => typeof id === 'string') as string[])
          : []);
  }

  // Toggle product selection
  toggleProduct(productId: string, event: any): void {
    const currentProductIds = this.getProductIdsFormValue();
    let updatedProductIds: string[];
    
    if (event.target.checked) {
      updatedProductIds = [...currentProductIds, productId];
    } else {
      updatedProductIds = currentProductIds.filter((id: string) => id !== productId);
    }
    
    this.categoryForm.patchValue({ productIds: updatedProductIds });
    this.categoryForm.get('productIds')?.markAsTouched();
    this.categoryForm.get('productIds')?.updateValueAndValidity();
  }

  // Pagination methods
  get paginatedCategories(): Category[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredCategories.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredCategories.length / this.itemsPerPage);
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  // Status toggle
  async toggleCategoryStatus(category: Category): Promise<void> {
    try {
      await this.categoryService.toggleCategoryStatus(category.id, !category.isActive);
      this.toastService.success(`Category ${!category.isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling category status:', error);
      this.toastService.error('Failed to update category status. Please try again.');
    }
  }

  // Format sub-products for display
  // Display sub-products as comma-separated from DB (as stored)
  formatSubProductsForDisplay(subProducts: string[] | any): string {
    console.log('formatSubProductsForDisplay called with:', subProducts);
    console.log('Type:', typeof subProducts, 'IsArray:', Array.isArray(subProducts));
    
    // Handle null/undefined
    if (!subProducts) {
      console.log('No subProducts provided');
      return 'None';
    }
    
    // Ensure it's an array
    let subProductsArray: string[] = [];
    if (Array.isArray(subProducts)) {
      subProductsArray = subProducts;
    } else if (typeof subProducts === 'string') {
      // If it's a string, try to split by comma or use as single item
      subProductsArray = subProducts.includes(',') 
        ? subProducts.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [subProducts];
    }
    
    console.log('Normalized subProducts array:', subProductsArray);
    
    if (subProductsArray.length === 0) {
      console.log('Empty subProducts array');
      return 'None';
    }
    
    // Display as comma-separated (as stored in DB)
    const result = subProductsArray.join(', ');
    console.log('Final subProducts result:', result);
    return result;
  }

  // Multilingual modal methods
  multilingualModalRef: any;
  openMultilingualModal(modal: any): void {
    this.populateMultilingualForm();
    this.multilingualModalRef = this.modalService.open(modal, { size: 'lg' });
  }

  private populateMultilingualForm(): void {
    // Clear existing FormArrays
    while (this.hiSubProductsFormArray.length !== 0) {
      this.hiSubProductsFormArray.removeAt(0);
    }
    while (this.mrSubProductsFormArray.length !== 0) {
      this.mrSubProductsFormArray.removeAt(0);
    }
    while (this.knSubProductsFormArray.length !== 0) {
      this.knSubProductsFormArray.removeAt(0);
    }

    if (this.isEditing && this.selectedCategory) {
      const i18n = this.selectedCategory.i18n || {};
      
      // Populate Hindi sub-products FormArray
      const hiSubProductsArray = Array.isArray(i18n['hi']?.subProducts) 
        ? i18n['hi'].subProducts 
        : (i18n['hi']?.subProducts ? [i18n['hi'].subProducts] : []);
      
      if (hiSubProductsArray.length === 0) {
        this.hiSubProductsFormArray.push(this.formBuilder.control('', Validators.required));
      } else {
        hiSubProductsArray.forEach((subProduct: string, index: number) => {
          const validators = index === 0 ? [Validators.required] : [];
          this.hiSubProductsFormArray.push(this.formBuilder.control(subProduct || '', validators));
        });
      }

      // Populate Marathi sub-products FormArray
      const mrSubProductsArray = Array.isArray(i18n['mr']?.subProducts) 
        ? i18n['mr'].subProducts 
        : (i18n['mr']?.subProducts ? [i18n['mr'].subProducts] : []);
      
      if (mrSubProductsArray.length === 0) {
        this.mrSubProductsFormArray.push(this.formBuilder.control('', Validators.required));
      } else {
        mrSubProductsArray.forEach((subProduct: string, index: number) => {
          const validators = index === 0 ? [Validators.required] : [];
          this.mrSubProductsFormArray.push(this.formBuilder.control(subProduct || '', validators));
        });
      }

      // Populate Kannada sub-products FormArray
      const knSubProductsArray = Array.isArray(i18n['kn']?.subProducts) 
        ? i18n['kn'].subProducts 
        : (i18n['kn']?.subProducts ? [i18n['kn'].subProducts] : []);
      
      if (knSubProductsArray.length === 0) {
        this.knSubProductsFormArray.push(this.formBuilder.control('', Validators.required));
      } else {
        knSubProductsArray.forEach((subProduct: string, index: number) => {
          const validators = index === 0 ? [Validators.required] : [];
          this.knSubProductsFormArray.push(this.formBuilder.control(subProduct || '', validators));
        });
      }

      // Populate other fields
      this.multilingualForm.patchValue({
        hi_name: i18n['hi']?.name || '',
        mr_name: i18n['mr']?.name || '',
        kn_name: i18n['kn']?.name || ''
      });
    } else {
      // Reset form for new categories - add one mandatory field to each FormArray
      this.hiSubProductsFormArray.push(this.formBuilder.control('', Validators.required));
      this.mrSubProductsFormArray.push(this.formBuilder.control('', Validators.required));
      this.knSubProductsFormArray.push(this.formBuilder.control('', Validators.required));
      this.multilingualForm.patchValue({
        hi_name: '',
        mr_name: '',
        kn_name: ''
      });
    }
  }

  saveMultilingualContent(): void {
    const formValue = this.multilingualForm.value;
    
    // Get sub-products from FormArrays and filter out empty values
    const hiSubProductsArray = this.hiSubProductsFormArray.value;
    const hiSubProducts = hiSubProductsArray
      .map((sp: string) => sp?.trim())
      .filter((sp: string) => sp && sp.length > 0);

    const mrSubProductsArray = this.mrSubProductsFormArray.value;
    const mrSubProducts = mrSubProductsArray
      .map((sp: string) => sp?.trim())
      .filter((sp: string) => sp && sp.length > 0);

    const knSubProductsArray = this.knSubProductsFormArray.value;
    const knSubProducts = knSubProductsArray
      .map((sp: string) => sp?.trim())
      .filter((sp: string) => sp && sp.length > 0);
    
    // Build i18n object
    const i18n: any = {};
    
    // Add Hindi translations if any field has content
    if (formValue.hi_name || hiSubProducts.length > 0) {
      i18n['hi'] = {
        name: formValue.hi_name || undefined,
        subProducts: hiSubProducts.length > 0 ? hiSubProducts : undefined
      };
    }
    
    // Add Marathi translations if any field has content
    if (formValue.mr_name || mrSubProducts.length > 0) {
      i18n['mr'] = {
        name: formValue.mr_name || undefined,
        subProducts: mrSubProducts.length > 0 ? mrSubProducts : undefined
      };
    }
    
    // Add Kannada translations if any field has content
    if (formValue.kn_name || knSubProducts.length > 0) {
      i18n['kn'] = {
        name: formValue.kn_name || undefined,
        subProducts: knSubProducts.length > 0 ? knSubProducts : undefined
      };
    }
    
    // Update the category form with i18n data
    this.categoryForm.patchValue({ i18n: Object.keys(i18n).length > 0 ? i18n : null });
    
    this.toastService.success('Multilingual content saved successfully!');
    this.multilingualModalRef.dismiss();
  }

  // Add a new Hindi sub-product field
  addHiSubProduct(): void {
    this.hiSubProductsFormArray.push(this.formBuilder.control(''));
    this.cdr.detectChanges();
  }

  // Remove a Hindi sub-product field (only if more than 1 exists)
  removeHiSubProduct(index: number): void {
    if (this.hiSubProductsFormArray.length > 1) {
      this.hiSubProductsFormArray.removeAt(index);
      this.cdr.detectChanges();
    }
  }

  // Check if delete button should be enabled for Hindi (only if more than 1 sub-product)
  canDeleteHiSubProduct(): boolean {
    return this.hiSubProductsFormArray.length > 1;
  }

  // Add a new Marathi sub-product field
  addMrSubProduct(): void {
    this.mrSubProductsFormArray.push(this.formBuilder.control(''));
    this.cdr.detectChanges();
  }

  // Remove a Marathi sub-product field (only if more than 1 exists)
  removeMrSubProduct(index: number): void {
    if (this.mrSubProductsFormArray.length > 1) {
      this.mrSubProductsFormArray.removeAt(index);
      this.cdr.detectChanges();
    }
  }

  // Check if delete button should be enabled for Marathi (only if more than 1 sub-product)
  canDeleteMrSubProduct(): boolean {
    return this.mrSubProductsFormArray.length > 1;
  }

  // Add a new Kannada sub-product field
  addKnSubProduct(): void {
    this.knSubProductsFormArray.push(this.formBuilder.control(''));
    this.cdr.detectChanges();
  }

  // Remove a Kannada sub-product field (only if more than 1 exists)
  removeKnSubProduct(index: number): void {
    if (this.knSubProductsFormArray.length > 1) {
      this.knSubProductsFormArray.removeAt(index);
      this.cdr.detectChanges();
    }
  }

  // Check if delete button should be enabled for Kannada (only if more than 1 sub-product)
  canDeleteKnSubProduct(): boolean {
    return this.knSubProductsFormArray.length > 1;
  }
}

